import { zExt } from "$root/third-party-integrations/zod";
import { zAgGridColumnState } from "$root/ui-lib/table/col-state-schema";
import { filterMap } from "$root/utils";
import {
	ComparisonOperatorDTOKeywordEnum,
	ViewModeEnum,
	type ComparisonOperatorDTO,
	type OperatorDTO,
	type SmartWorkspaceDTO,
	type ParameterizedConceptDTO,
	type SmartWorkspacePreferencesColumnDTO,
	type SmartWorkspacePreferencesDataDTO,
	type SmartWorkspaceDTOChart,
} from "@eu.mdotm.api/smart-workspaces";
import { chartTypeSortingMode, isKnownChartType } from "../chart-type";
import { Result } from "@mdotm/utils/runtime";
import type { ColumnPinnedType, ColumnState } from "ag-grid-community";
import z, { type TypeOf } from "zod";
import { type ParameterizedConceptDTOWithMandatoryUuid, type Workspace, pageSizeOptions } from "./context";
import Immutable from "immutable";
import { type WorkspaceState, privateConcepts, resolveConcepts } from "../state/state";

const initialPaginationSize = pageSizeOptions.at(3)!;

// --- Preference schemas ---

const gridPreferencesV1 = zExt.versioned(
	1,
	z.object({
		columns: zExt.maybe(z.array(zAgGridColumnState)),
	}),
);
const gridPreferencesLatest = gridPreferencesV1;

import { type chartPreferenceContent, chartPreferencesLatest } from "./preferences-schemas";

// --- DTO → Context (deserialization) ---

/**
 * READ WHAT THE WIRE SAYS, DON'T FAIL THE PAGE. Everything here is a *shape* check: a value that is not
 * the type it claims to be is dropped, so a workspace persisted by something other than this app cannot
 * take the screen down to the generic error fallback. `keyword` is a bare `string` on the wire and was
 * cast unchecked, and `predicate.conditions` / `series` are read with `.flatMap` inside the render-time
 * mapping of `useMappedQueries` — so a non-array there did not even reach the error fallback, it threw
 * into the GlobalErrorBoundary.
 *
 * **References are not checked here.** A uuid that resolves to nothing is repaired one layer up, by
 * `state/ingest.ts`, which drops the column, filter, series or sort behind it and *says what it changed*.
 * That is the one place allowed to see a broken workspace, and duplicating it here would mean two places
 * deciding what a user silently loses — `ctx/adapter.test.ts` pins that this layer keeps what it is given.
 *
 * One case escapes both, and predates them: a predicate that is not a flat `and` loses shape rather than
 * references — see the TODO on `filters` below.
 */
function smartWorkspaceToContext(params?: {
	workspace?: SmartWorkspaceDTO;
	sharedParametrizedConcepts?: ParameterizedConceptDTO[];
}): Workspace {
	const { sharedParametrizedConcepts, workspace } = params ?? {};
	const contextAdapter = smartWorkspaceToContextHelper();

	if (!workspace) {
		return {
			view: ViewModeEnum.Table,
			filters: [],
			charts: [],
			columns: null,
			search: "",
			privateParametrizedConcepts: [],
			sort: null,
			pagination: { page: 1, size: initialPaginationSize, total: 0 },
			selection: [],
			workspace: undefined,
			sharedParametrizedConcepts: contextAdapter.sharedParametrizedConcepts(sharedParametrizedConcepts),
		};
	}

	const sanitizedSharedParametrizedConcepts = contextAdapter.sharedParametrizedConcepts(sharedParametrizedConcepts);
	const charts = contextAdapter.chart(workspace);
	return {
		view: workspace.viewMode ?? ViewModeEnum.Table,
		filters: contextAdapter.filters(workspace),
		charts,
		columns: contextAdapter.columns({ workspace, sharedParametrizedConcepts: sanitizedSharedParametrizedConcepts }),
		search: "",
		privateParametrizedConcepts: contextAdapter.parameterizedConcepts(workspace),
		sort: contextAdapter.sort(workspace, charts),
		pagination: { page: 1, size: workspace.grid?.preferences?.data?.pageSize ?? initialPaginationSize, total: 0 },
		selection: [],
		workspace: workspace,
		sharedParametrizedConcepts: sanitizedSharedParametrizedConcepts,
	};
}

/**
 * Read a chart's persisted `preferences` envelope, falling back to a visible chart when it
 * doesn't match the schema. Never throws: `preferences` is free-form `object` on the wire and
 * the platform is no longer its only writer (the Sphere AI workspace tools persist charts too),
 * so an off-schema value must degrade to a default rather than take the whole page down with it.
 */
function readChartPreferences(preferences: unknown): TypeOf<typeof chartPreferenceContent> {
	return Result.fromThrower(() => chartPreferencesLatest.parse(preferences))
		.map((x) => x.data)
		.unwrapOr({ hide: false });
}

const comparisonKeywords = new Set<string>(Object.values(ComparisonOperatorDTOKeywordEnum));

/**
 * A comparison this workspace can actually run, or nothing.
 *
 * `keyword` is a bare `string` on the wire and used to be cast straight into the enum, so a value the API
 * has no such thing as reached every data request and failed it. Whether the concept it names *exists* is
 * a different question and not asked here — that is a reference, and `state/ingest.ts` owns those.
 */
function comparisonToFilter(comparison: ComparisonOperatorDTO): [Workspace["filters"][number]] | [] {
	const parameterizedConceptUuid = comparison.parameterizedConceptUuid;
	if (
		parameterizedConceptUuid == null ||
		!comparisonKeywords.has(comparison.keyword) ||
		!Array.isArray(comparison.arguments)
	) {
		return [];
	}

	return [
		{
			arguments: comparison.arguments,
			parameterizedConceptUuid,
			keyword: comparison.keyword as ComparisonOperatorDTOKeywordEnum,
		},
	];
}

function smartWorkspaceToContextHelper() {
	/**
	 * TODO(MDOT-24816): `predicate` is a TREE — `OperatorDTO` is recursive, so a LOGICAL's conditions
	 * may hold further LOGICALs — and its `keyword` may be `or`. This reads only the top level and
	 * ignores the keyword: a nested branch is dropped, and an `or` is read as (and, on the next
	 * autosave, rewritten to) an `and`, which quietly changes what the workspace matches while its
	 * filters still look right.
	 *
	 * The proper fix is to walk the tree and model the operator in the context, but the filter UI has
	 * no notion of one — `WorkspaceFilterSettings` renders a flat list with no and/or affordance, and
	 * `filtersToPredicate` can only emit a flat `and` — so honouring `or` here would produce a filter
	 * set the user can see and not express. That's a UI change first, hence not done here.
	 *
	 * Latent rather than live for now: every writer emits a flat `and` (this file's
	 * `filtersToPredicate`, and sphere-mcp's `buildPredicate` — its nested `andPredicates` is built
	 * for one-off data queries and never persisted). "No writer does this today" is exactly the
	 * assumption that produced the dangling references above, so treat it as a matter of time.
	 */
	function filters(workspace: SmartWorkspaceDTO | undefined): Workspace["filters"] {
		if (workspace?.predicate?.type === "LOGICAL") {
			const conditions = workspace.predicate.conditions;
			if (!Array.isArray(conditions)) {
				return [];
			}
			return conditions.flatMap((item): [Workspace["filters"][number]] | [] =>
				item.type === "COMPARISON" ? comparisonToFilter(item) : [],
			);
		}

		if (workspace?.predicate?.type === "COMPARISON") {
			return comparisonToFilter(workspace.predicate);
		}
		return [];
	}

	/**
	 * The persisted charts this build can draw, and the only door `workspace.charts` comes through.
	 *
	 * A chart whose `chartType` this build does not know is dropped here rather than at each place that
	 * branches on the type: the enum is the backend's, so a value can arrive that no switch here answers.
	 * Dropping it at ingestion means it is *gone* — it is not in the context, so the next `syncWorkspace`
	 * persists a workspace without it. That is the deliberate trade for having one place to reason about
	 * instead of a guard at every consumer.
	 */
	function knownCharts(workspace?: SmartWorkspaceDTO): SmartWorkspaceDTOChart[] {
		return (workspace?.charts ?? []).filter((c) => isKnownChartType(c.chartType));
	}

	function chart(workspace?: SmartWorkspaceDTO): Workspace["charts"] {
		// `chartType`, `breakdownProperties` and `distributionProperties` round-trip through the spread —
		// `chartType` is required on the wire and read as the wire spells it, so there is nothing to
		// default or translate here.
		//
		// `series` is normalized to an array and never filtered: which of its uuids resolve is a reference
		// question, and `state/ingest.ts` answers it (pruning the series, and dropping a chart left with
		// none). The guard is here because everything downstream — that repair included — calls array
		// methods on it, so a non-array would throw out of the render-time mapping rather than degrade.
		return knownCharts(workspace).map((c) => ({
			...c,
			series: Array.isArray(c.series) ? c.series : [],
			preferences: readChartPreferences(c.preferences),
		}));
	}

	function columns({
		sharedParametrizedConcepts,
		workspace,
	}: {
		workspace?: SmartWorkspaceDTO;
		sharedParametrizedConcepts?: ParameterizedConceptDTOWithMandatoryUuid[];
	}): Workspace["columns"] {
		const preferences = workspace?.grid?.preferences?.data;

		const allParametrizedConceptsMap: Record<string, ParameterizedConceptDTOWithMandatoryUuid> = {};
		const sharedParametrizedConceptsMap: Record<string, ParameterizedConceptDTOWithMandatoryUuid> = {};

		workspace?.parameterizedConcepts?.forEach((parameterizedConcept) => {
			if (parameterizedConcept.uuid) {
				allParametrizedConceptsMap[parameterizedConcept.uuid] = {
					...parameterizedConcept,
					uuid: parameterizedConcept.uuid,
				};
			}
		});

		sharedParametrizedConcepts?.forEach((sharedParametrizedConcept) => {
			sharedParametrizedConceptsMap[sharedParametrizedConcept.uuid] = sharedParametrizedConcept;
			allParametrizedConceptsMap[sharedParametrizedConcept.uuid] = sharedParametrizedConcept;
		});

		const sharedParametrizedConceptsUuids = sharedParametrizedConcepts?.map(
			(parameterizedConcept) => parameterizedConcept.uuid,
		);
		if (!preferences?.columns) {
			const gridProjection = Immutable.OrderedSet([
				...(workspace?.grid?.projection ?? []),
				...(sharedParametrizedConceptsUuids ?? []),
			]).toArray();
			const sharedParametrizedConceptNotInProjections =
				workspace?.grid?.projection.reduce((acc, parametrizedConceptUuid) => {
					delete acc[parametrizedConceptUuid];
					return acc;
				}, sharedParametrizedConceptsMap) ?? {};

			const columns = gridProjection.flatMap(
				(parametrizedConceptUuid): [NonNullable<Workspace["columns"]>[number]] | [] => {
					const concept = allParametrizedConceptsMap[parametrizedConceptUuid];
					if (!concept) {
						return [];
					}

					return [
						{
							colId: parametrizedConceptUuid,
							hide: sharedParametrizedConceptNotInProjections[parametrizedConceptUuid] != null,
							width: 200,
							pivot: false,
							rowGroup: false,
							pinned: concept.conceptId === "systemId: name" ? "left" : undefined,
						},
					];
				},
			);
			return columns ?? null;
		}

		const columnsIds = Immutable.OrderedSet([
			...(preferences.columns?.flatMap((col): [string] | [] => (col.colId ? [col.colId] : [])) ?? []),
			...(sharedParametrizedConceptsUuids ?? []),
		]).toArray();

		const preferencesMap =
			preferences.columns?.reduce<Record<string, NonNullable<SmartWorkspacePreferencesDataDTO["columns"]>[number]>>(
				(acc, preference) => {
					if (preference.colId) {
						acc[preference.colId] = preference;
					}
					return acc;
				},
				{},
			) ?? {};

		return (
			columnsIds.flatMap((colId): [NonNullable<Workspace["columns"]>[number]] | [] => {
				const parametrizedConcept = allParametrizedConceptsMap[colId];
				if (parametrizedConcept == null) {
					return [];
				}

				const agColumns = preferencesMap[colId];
				if (agColumns != null) {
					// sort is sourced from sort$ (see the sort() deserializer below), never from column layout —
					// column state stays layout-only so the grid can only ever show a single sort arrow.
					const { sort: _sort, pinned, ...rest } = agColumns;
					return [
						{
							...rest,
							colId,
							hide: !!rest.hide,
							pivot: !!rest.pivot,
							rowGroup: !!rest.rowGroup,
							...(pinned ? { pinned: pinned as ColumnPinnedType } : {}),
						},
					];
				}

				return [
					{
						colId,
						hide: true,
						width: 200,
						pivot: false,
						rowGroup: false,
						pinned: false,
					},
				];
			}) ?? null
		);
	}

	function parameterizedConcepts(workspace?: SmartWorkspaceDTO): Workspace["privateParametrizedConcepts"] {
		return (
			workspace?.parameterizedConcepts?.flatMap(
				(parameterizedConcept): [ParameterizedConceptDTOWithMandatoryUuid] | [] =>
					parameterizedConcept.uuid ? [{ ...parameterizedConcept, uuid: parameterizedConcept.uuid }] : [],
			) ?? []
		);
	}

	function sort(workspace: SmartWorkspaceDTO | undefined, charts: Workspace["charts"]): Workspace["sort"] {
		let sort: Workspace["sort"] = null;
		const preferences = workspace?.grid?.preferences?.data;
		if (preferences == null) {
			return null;
		}

		const tableSorting = preferences.columns?.find((x) => x.sort != null);
		// Whether that column is still there is a reference question — `state/ingest.ts` clears a sort whose
		// concept resolves to nothing, and says so.
		if (tableSorting?.sort && tableSorting.colId != null) {
			sort = { direction: tableSorting.sort === "ASC" ? "ASC" : "DESC", parameterizedConceptUuid: tableSorting.colId };
		}

		// Only a column chart (`HISTOGRAM`) may drive the single global workspace sort — it is the one the
		// backend's comparator can order (see `chartTypeSortingMode`). A stacked column persists a `sort`
		// direction too, but its own client-side one over a breakdown category, so it must not hijack the
		// table's sort; the distribution and the line persist none at all.
		//
		// Read off the already-sanitized charts, so a sort persisted on a chart that got dropped above cannot
		// come back in through here — and their preferences were parsed once, at ingestion, which is also what
		// keeps an unparseable envelope from being able to fail the page.
		const sortedChart = filterMap(charts, (candidate) =>
			chartTypeSortingMode(candidate.chartType) === "WORKSPACE" && candidate.preferences?.sort != null
				? [{ chart: candidate, preferences: candidate.preferences }]
				: [],
		).at(0);

		if (sortedChart?.preferences.sort) {
			const chartSeriesParameterizedConcepts = workspace?.parameterizedConcepts?.filter((concept) =>
				concept.uuid ? sortedChart.chart.series.includes(concept.uuid) : false,
			);

			const chartParameterizedConceptUuid = chartSeriesParameterizedConcepts?.find((concept) =>
				sortedChart.preferences.highlightProposal ? concept.source === "PROPOSAL" : concept.source === "CURRENT",
			)?.uuid;

			if (chartParameterizedConceptUuid) {
				sort = { direction: sortedChart.preferences.sort, parameterizedConceptUuid: chartParameterizedConceptUuid };
			}
		}

		return sort;
	}

	function sharedParametrizedConcepts(
		sharedParametrizedConcepts?: ParameterizedConceptDTO[],
	): ParameterizedConceptDTOWithMandatoryUuid[] {
		if (!sharedParametrizedConcepts) {
			return [];
		}
		return sharedParametrizedConcepts.filter(
			(parametrizedConcept): parametrizedConcept is ParameterizedConceptDTOWithMandatoryUuid =>
				parametrizedConcept.uuid != null,
		);
	}

	return { filters, chart, columns, parameterizedConcepts, sort, sharedParametrizedConcepts };
}

// --- State → DTO (serialization) ---

/**
 * The workspace as it goes to the wire.
 *
 * Everything that makes this file worth carrying over rather than rewriting — the versioned preference
 * envelopes, the ag-grid `ColumnState` round trip, the projection filtered through the pool — lives in the
 * one converter below, which used to have three entry points (one reading ten atoms, one a hook over the
 * same ten, this one) and now has one.
 */
function stateToSmartWorkspace(state: WorkspaceState): SmartWorkspaceDTO {
	return contextToSmartWorkspaceConverter(
		state.meta.document,
		privateConcepts(state.concepts),
		resolveConcepts(state.concepts),
		state.content.charts,
		state.content.columns,
		state.content.filters,
		state.content.view,
		{ page: state.ui.page, size: state.content.pageSize, total: state.ui.resultTotal },
		state.content.sort,
	);
}

function contextToSmartWorkspaceConverter(
	workspace: SmartWorkspaceDTO | undefined,
	parameterizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[],
	allParametrizedConcepts: ParameterizedConceptDTOWithMandatoryUuid[],
	charts: (SmartWorkspaceDTOChart & { preferences?: TypeOf<typeof chartPreferenceContent> })[],
	columns: ColumnState[] | null,
	filters: Workspace["filters"],
	view: Workspace["view"],
	pagination: Workspace["pagination"],
	sort: Workspace["sort"],
): SmartWorkspaceDTO {
	return {
		...(workspace ?? {}),
		parameterizedConcepts,
		charts: charts.map((c) => ({
			...c,
			preferences: {
				version: 1,
				// Restated field by field rather than spread, so the versioned envelope can only ever carry
				// what the current schema declares. Keyed on `keyof` because every one of them is optional:
				// dropping one here would look like a chart whose user preference was never set.
				data: {
					hide: c.preferences?.hide,
					sort: c.preferences?.sort,
					sortCategory: c.preferences?.sortCategory,
					highlightProposal: c.preferences?.highlightProposal,
					showMeanLines: c.preferences?.showMeanLines,
					showNormalFit: c.preferences?.showNormalFit,
				} satisfies Record<keyof TypeOf<typeof chartPreferenceContent>, unknown>,
			} satisfies z.infer<typeof chartPreferencesLatest>,
		})),
		grid: {
			projection: filterMap(allParametrizedConcepts, (concept) =>
				columns?.some((c) => c.colId === concept.uuid) ? [concept.uuid] : [],
			),
			preferences: {
				version: 1,
				data: {
					// The ag-grid `ColumnState` → DTO half of the column-layout round-trip. Every field of the DTO
					// is optional, so an omitted one reads back as "the user never set it" and the layout is
					// silently lost — hence the `keyof` guard rather than the return annotation alone.
					columns: columns?.map(
						(col): SmartWorkspacePreferencesColumnDTO =>
							({
								colId: col.colId as string | undefined,
								hide: col.hide || undefined,
								pinned: (col.pinned || undefined) as string | undefined,
								pivot: col.pivot || undefined,
								rowGroup: col.rowGroup || undefined,
								width: col.width,
								// sort$ is the single source/owner of sort persistence: write it onto the one matching
								// column instead of reading col.sort (column layout is sort-free, see toContext.columns).
								sort:
									sort != null && col.colId != null && sort.parameterizedConceptUuid === col.colId
										? sort.direction
										: undefined,
							}) satisfies Record<keyof SmartWorkspacePreferencesColumnDTO, unknown>,
					),
					pageSize: pagination.size,
				}, //  satisfies z.infer<typeof gridPreferencesLatest>,,
			},
		},
		dataProvider: { dataProviderId: "investment", parameters: {} },
		predicate: filtersToPredicate(filters),
		viewMode: view,
	};
}

/**
 * The filters as one predicate — `SmartWorkspaceDTO.predicate`, and nothing else. Exported because a data
 * request filters by the same predicate plus the search box (`selectDataPredicate`), and deriving it twice
 * is how the two would come to disagree about what the workspace is filtered to.
 */
export function filtersToPredicate(filters: Workspace["filters"]): OperatorDTO | undefined {
	if (filters.length === 0) {
		return undefined;
	}

	// `parameterizedConceptUuid` is the only optional field of a `ComparisonOperatorDTO` and the one that
	// says *what* is being compared, so the `keyof` guard is what stops a comparison from being persisted
	// against nothing.
	if (filters.length === 1) {
		const filter = filters.at(0)!;
		return {
			type: "COMPARISON" as const,
			arguments: filter.arguments,
			keyword: filter.keyword,
			parameterizedConceptUuid: filter.parameterizedConceptUuid,
		} satisfies Record<keyof ComparisonOperatorDTO, unknown>;
	}

	return {
		type: "LOGICAL",
		keyword: "and",
		conditions: filters.map(
			(filter) =>
				({
					type: "COMPARISON" as const,
					arguments: filter.arguments,
					keyword: filter.keyword,
					parameterizedConceptUuid: filter.parameterizedConceptUuid,
				}) satisfies Record<keyof ComparisonOperatorDTO, unknown>,
		),
	};
}

export const workspaceAdapter = {
	toContext: smartWorkspaceToContext,
	fromState: stateToSmartWorkspace,
};
